/**
 * Shared building blocks for Pathfinder's floating map HUD cards (Story,
 * Sentinel, Legend): theme tokens, the style popover, drag-to-move, and the
 * minimized chip. State lives in narrativeStore so every card shares one look
 * and remembers its layout.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallToAction as DockIcon,
  Code as HtmlIcon,
  ContentCopy as CopyIcon,
  Description as MarkdownIcon,
  DragIndicator as DragIcon,
  Image as PngIcon,
  IosShare as ExportIcon,
  Palette as PaletteIcon,
  PictureAsPdf as PdfIcon,
  PictureInPictureAlt as FloatIcon,
  RestartAlt as ResetIcon,
  Slideshow as PptxIcon,
  TextDecrease as TextSmallerIcon,
  TextIncrease as TextLargerIcon,
} from '@mui/icons-material';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  DEFAULT_HUD_STYLE,
  HUD_ACCENTS,
  HUD_FONT_SCALE_MAX,
  HUD_FONT_SCALE_MIN,
  useNarrativeStore,
  type HudDockablePanelId,
  type HudLayout,
  type HudPanelId,
  type HudStyle,
  type HudTheme,
} from '../../stores/narrativeStore';
import {
  copyDocToClipboard,
  downloadDocHtml,
  downloadDocMarkdown,
  downloadDocPdf,
  downloadHudCardPng,
  exportHudDocToPowerPoint,
  hudCardToPngDataUrl,
  type HudExportDoc,
} from './pathfinderHudExport';

export interface HudTokens {
  bg: [number, number, number];
  text: string;
  muted: string;
  border: string;
  font: string;
  shadow: string;
  blur: number;
  /** Text color to use on top of the accent color. */
  onAccent: string;
  /** Secondary solid colour: travel segments (dwells use the accent). */
  secondary: string;
}

// Dark, desaturated presets. Endurion = brand charcoal/navy with steel borders;
// Tactical = near-black olive with a mono face; Slate = neutral gunmetal;
// Paper = light, for print-like readability.
export const HUD_THEMES: Record<HudTheme, { label: string; tokens: HudTokens }> = {
  endurion: {
    label: 'Endurion',
    tokens: {
      bg: [20, 21, 27],
      text: '#e6e8ec',
      muted: 'rgba(174,183,194,0.75)',
      border: 'rgba(174,183,194,0.28)',
      font: 'inherit',
      shadow: '0 14px 40px rgba(0,0,0,0.55)',
      blur: 8,
      onAccent: '#ffffff',
      secondary: '#AEB7C2',
    },
  },
  tactical: {
    label: 'Tactical',
    tokens: {
      bg: [9, 12, 9],
      text: '#cfd6c4',
      muted: 'rgba(207,214,196,0.6)',
      border: 'rgba(124,143,58,0.4)',
      font: '"JetBrains Mono", "Roboto Mono", Consolas, monospace',
      shadow: '0 0 0 1px rgba(124,143,58,0.15), 0 12px 32px rgba(0,0,0,0.65)',
      blur: 4,
      onAccent: '#0a0d08',
      secondary: '#B5B99A',
    },
  },
  slate: {
    label: 'Slate',
    tokens: {
      bg: [28, 31, 36],
      text: '#dde1e6',
      muted: 'rgba(221,225,230,0.6)',
      border: 'rgba(255,255,255,0.1)',
      font: 'inherit',
      shadow: '0 12px 36px rgba(0,0,0,0.5)',
      blur: 8,
      onAccent: '#ffffff',
      secondary: '#9AA3AD',
    },
  },
  paper: {
    label: 'Paper',
    tokens: {
      bg: [246, 245, 241],
      text: '#1f232b',
      muted: 'rgba(31,35,43,0.62)',
      border: 'rgba(0,0,0,0.14)',
      font: 'inherit',
      shadow: '0 10px 30px rgba(0,0,0,0.25)',
      blur: 8,
      onAccent: '#ffffff',
      secondary: '#5B6470',
    },
  },
};

/** Readable text color on top of an accent (dark text for light accents). */
const onAccentFor = (accent: string, fallback: string) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(accent);
  if (!m) return fallback;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma > 150 ? '#14151b' : '#ffffff';
};

/** Resolved style helpers for a HUD card. */
export function useHudStyle() {
  const style = useNarrativeStore((s) => s.hudStyle);
  return useMemo(() => {
    const base = HUD_THEMES[style.theme].tokens;
    const [r, g, b] = base.bg;
    const accentRaw = style.accent;
    // White / steel accents on the paper theme would vanish; fall back to ink.
    const isLight = style.theme === 'paper';
    const accent =
      isLight && ['#ffffff', '#aeb7c2', '#c2a878'].includes(accentRaw.toLowerCase()) ? '#1f232b' : accentRaw;
    const tokens: HudTokens = { ...base, onAccent: onAccentFor(accent, base.onAccent) };
    // Dwell = accent, travel = secondary. If the user picked the secondary as
    // their accent, fall back to amber so the two stay distinguishable.
    const secondary = base.secondary.toLowerCase() === accent.toLowerCase() ? '#D4891B' : base.secondary;
    const bg = `rgba(${r}, ${g}, ${b}, ${style.opacity})`;
    const solidBg = `rgb(${r}, ${g}, ${b})`;
    const inset = isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)';
    const fs = (rem: number) => `${(rem * style.fontScale).toFixed(3)}rem`;
    const surfaceSx: SxProps<Theme> = {
      bgcolor: bg,
      color: tokens.text,
      fontFamily: tokens.font,
      border: `1px solid ${tokens.border}`,
      borderRadius: 1.5,
      boxShadow: tokens.shadow,
      backdropFilter: `blur(${tokens.blur}px)`,
    };
    return { style, tokens, accent, secondary, bg, solidBg, inset, fs, surfaceSx };
  }, [style]);
}

export type HudStyleInfo = ReturnType<typeof useHudStyle>;

/** Portal target that works while the map column is fullscreen. */
export const hudPortalContainer = () => (document.fullscreenElement as HTMLElement | null) ?? document.body;

/**
 * Drag-to-move for a HUD card. The card is absolutely positioned inside the
 * map container; when the user has not dragged it yet, `defaultSx` anchors it.
 */
export function useHudDrag(id: HudPanelId, defaultSx: SxProps<Theme>) {
  const stored = useNarrativeStore((s) => s.panelPositions[id]);
  const setPosition = useNarrativeStore((s) => s.setPanelPosition);
  const [live, setLive] = useState<{ left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    left: number;
    top: number;
    maxLeft: number;
    maxTop: number;
  } | null>(null);

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // Only primary button; ignore clicks on buttons inside the handle.
    if (e.button !== 0) return;
    // Popovers / menus opened from header buttons are portaled, but React
    // still bubbles their events here. Only react to real handle presses.
    if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return;
    if ((e.target as HTMLElement).closest('button, [role="button"], input, .MuiToggleButtonGroup-root')) return;
    const card = (e.currentTarget as HTMLElement).closest('[data-hud-card]') as HTMLElement | null;
    const parent = card?.offsetParent as HTMLElement | null;
    if (!card || !parent) return;
    const cardRect = card.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      left: cardRect.left - parentRect.left,
      top: cardRect.top - parentRect.top,
      maxLeft: Math.max(0, parentRect.width - cardRect.width),
      maxTop: Math.max(0, parentRect.height - cardRect.height),
    };
    setLive({ left: dragRef.current.left, top: dragRef.current.top });
    setDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, []);

  const onHandlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const left = Math.min(d.maxLeft, Math.max(0, d.left + (e.clientX - d.startX)));
    const top = Math.min(d.maxTop, Math.max(0, d.top + (e.clientY - d.startY)));
    setLive({ left, top });
  }, []);

  const onHandlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      setDragging(false);
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      setLive((pos) => {
        if (pos) setPosition(id, pos);
        return null;
      });
    },
    [id, setPosition],
  );

  const pos = live ?? stored;
  const positionSx: SxProps<Theme> = pos
    ? { position: 'absolute', left: pos.left, top: pos.top, right: 'auto', bottom: 'auto', transform: 'none' }
    : ({ position: 'absolute', ...(defaultSx as object) } as SxProps<Theme>);

  const handleProps = {
    onPointerDown: onHandlePointerDown,
    onPointerMove: onHandlePointerMove,
    onPointerUp: onHandlePointerUp,
    onPointerCancel: onHandlePointerUp,
    onDoubleClick: (e: React.MouseEvent<HTMLElement>) => {
      if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest('button, [role="button"], input')) return;
      setPosition(id, null);
    },
    style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none' as const },
  };

  return { positionSx, handleProps, dragging, hasCustomPosition: Boolean(stored) };
}

/** Small drag glyph for card headers. */
export const HudDragGlyph: React.FC<{ color: string }> = ({ color }) => (
  <Tooltip title="Drag to move · double-click to reset" enterDelay={600}>
    <DragIcon sx={{ fontSize: 16, color, opacity: 0.7, flexShrink: 0 }} />
  </Tooltip>
);

/** Palette icon button that opens the shared style popover. */
export const HudStyleButton: React.FC<{
  sx?: SxProps<Theme>;
  /** Which sliders to show; 'width' is irrelevant for the legend. */
  showWidth?: boolean;
  showStats?: boolean;
}> = ({ sx, showWidth = true, showStats = false }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Tooltip title="Style">
        <IconButton size="small" sx={sx} onClick={(e) => setAnchor(e.currentTarget)}>
          <PaletteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <HudStylePopover
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        showWidth={showWidth}
        showStats={showStats}
      />
    </>
  );
};

export const HudStylePopover: React.FC<{
  anchorEl: HTMLElement | null;
  onClose: () => void;
  showWidth?: boolean;
  showStats?: boolean;
}> = ({ anchorEl, onClose, showWidth = true, showStats = false }) => {
  const style = useNarrativeStore((s) => s.hudStyle);
  const updateStyle = useNarrativeStore((s) => s.updateHudStyle);
  const resetStyle = useNarrativeStore((s) => s.resetHudStyle);
  const resetPositions = useNarrativeStore((s) => s.resetPanelPositions);
  const hasPositions = useNarrativeStore(
    (s) => Object.values(s.panelPositions).some(Boolean) || Object.values(s.panelLayout).some((l) => l === 'dock'),
  );

  // Pin the popover where it opened. MUI re-positions a Popover against its
  // anchor on every render, so a live width/text-size change would slide the
  // popover (and the slider rail) under the pointer mid-drag.
  const anchorPosition = useMemo(() => {
    if (!anchorEl) return undefined;
    const r = anchorEl.getBoundingClientRect();
    return { top: r.bottom, left: r.right };
  }, [anchorEl]);

  const sliders: Array<{ k: keyof HudStyle; label: string; min: number; max: number; step: number }> = [
    ...(showWidth ? [{ k: 'width' as const, label: 'Width', min: 280, max: 600, step: 10 }] : []),
    { k: 'opacity', label: 'Opacity', min: 0.4, max: 1, step: 0.02 },
    { k: 'fontScale', label: 'Text size', min: HUD_FONT_SCALE_MIN, max: HUD_FONT_SCALE_MAX, step: 0.05 },
  ];

  const toggleSx = {
    mt: 0.5,
    mb: 1.25,
    '& .MuiToggleButton-root': { color: '#ccc', textTransform: 'none', py: 0.25, fontSize: '0.7rem' },
    '& .Mui-selected': { color: '#fff !important', bgcolor: `${style.accent}33 !important` },
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      container={hudPortalContainer}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ zIndex: 4000 }}
      PaperProps={{ sx: { p: 1.5, width: 260, bgcolor: 'rgba(24,26,32,0.97)', color: '#fff' } }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
        THEME
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        value={style.theme}
        onChange={(_e, v) => v && updateStyle({ theme: v })}
        sx={toggleSx}
      >
        {(Object.keys(HUD_THEMES) as HudTheme[]).map((t) => (
          <ToggleButton key={t} value={t}>
            {HUD_THEMES[t].label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
        ACCENT
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, mb: 1.25 }}>
        {HUD_ACCENTS.map((c) => (
          <Box
            key={c}
            onClick={() => updateStyle({ accent: c })}
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: c,
              cursor: 'pointer',
              border: `2px solid ${style.accent === c ? '#fff' : 'rgba(255,255,255,0.15)'}`,
              boxShadow: style.accent === c ? `0 0 0 2px ${c}` : 'none',
            }}
          />
        ))}
      </Box>

      {sliders.map((s) => (
        <Box key={s.k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ width: 64, color: '#bbb' }}>
            {s.label}
          </Typography>
          <Slider
            size="small"
            min={s.min}
            max={s.max}
            step={s.step}
            value={style[s.k] as number}
            onChange={(_e, v) => updateStyle({ [s.k]: v as number })}
            sx={{ color: style.accent }}
          />
        </Box>
      ))}

      {showStats && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#bbb' }}>
            Story stats strip
          </Typography>
          <Switch
            size="small"
            checked={style.showStats}
            onChange={(_e, v) => updateStyle({ showStats: v })}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          onClick={resetStyle}
          disabled={JSON.stringify(style) === JSON.stringify(DEFAULT_HUD_STYLE)}
          sx={{ color: '#bbb', textTransform: 'none', fontSize: '0.7rem' }}
        >
          Reset style
        </Button>
        <Button
          size="small"
          onClick={resetPositions}
          disabled={!hasPositions}
          sx={{ color: '#bbb', textTransform: 'none', fontSize: '0.7rem' }}
        >
          Reset layout
        </Button>
      </Box>
    </Popover>
  );
};

/** A− / A+ text size nudgers for card headers. */
export const HudTextSizeButtons: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
  const scale = useNarrativeStore((s) => s.hudStyle.fontScale);
  const step = useNarrativeStore((s) => s.stepFontScale);
  return (
    <>
      <Tooltip title={`Smaller text (${Math.round(scale * 100)}%)`}>
        <span>
          <IconButton size="small" sx={sx} onClick={() => step(-0.1)} disabled={scale <= HUD_FONT_SCALE_MIN + 1e-6}>
            <TextSmallerIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Larger text (${Math.round(scale * 100)}%)`}>
        <span>
          <IconButton size="small" sx={sx} onClick={() => step(0.1)} disabled={scale >= HUD_FONT_SCALE_MAX - 1e-6}>
            <TextLargerIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

/** Float / dock-to-bottom toggle. */
export const HudLayoutButton: React.FC<{ id: HudDockablePanelId; sx?: SxProps<Theme> }> = ({ id, sx }) => {
  const layout = useNarrativeStore((s) => s.panelLayout[id]);
  const setLayout = useNarrativeStore((s) => s.setPanelLayout);
  const next: HudLayout = layout === 'dock' ? 'float' : 'dock';
  return (
    <Tooltip title={layout === 'dock' ? 'Float as a card' : 'Dock along the bottom of the map'}>
      <IconButton size="small" sx={sx} onClick={() => setLayout(id, next)}>
        {layout === 'dock' ? <FloatIcon sx={{ fontSize: 16 }} /> : <DockIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  );
};

/**
 * Export menu: copy / markdown / HTML / PDF / PNG / PowerPoint. `getDoc`
 * builds the text document; `cardRef` is the card element for PNG capture.
 */
export const HudExportButton: React.FC<{
  sx?: SxProps<Theme>;
  getDoc: () => HudExportDoc;
  cardRef: React.RefObject<HTMLElement | null>;
  solidBackground: string;
  /** Accent hex used for HTML / PDF / PPTX styling. */
  accent?: string;
  disabled?: boolean;
}> = ({ sx, getDoc, cardRef, solidBackground, accent = '#FF2332', disabled }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const close = () => setAnchor(null);
  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 1800);
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      console.error('HUD export failed:', e);
      flash('Export failed');
    } finally {
      setBusy(null);
      close();
    }
  };

  return (
    <>
      <Tooltip title={notice ?? 'Export'}>
        <span>
          <IconButton size="small" sx={sx} onClick={(e) => setAnchor(e.currentTarget)} disabled={disabled || Boolean(busy)}>
            <ExportIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        container={hudPortalContainer}
        sx={{ zIndex: 4000 }}
        PaperProps={{ sx: { bgcolor: 'rgba(24,26,32,0.97)', color: '#fff', minWidth: 220 } }}
      >
        <MenuItem
          dense
          onClick={() =>
            run('copy', async () => {
              flash((await copyDocToClipboard(getDoc())) ? 'Copied' : 'Copy blocked');
            })
          }
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy as text" secondary="Paste into notes, email, slides" secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
        </MenuItem>
        <MenuItem dense onClick={() => run('md', async () => downloadDocMarkdown(getDoc()))}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><MarkdownIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download Markdown (.md)" />
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            run('html', async () => {
              const doc = getDoc();
              const img = cardRef.current ? await hudCardToPngDataUrl(cardRef.current, solidBackground) : null;
              await downloadDocHtml(doc, { cardImageDataUrl: img, includeMap: true, accent });
            })
          }
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><HtmlIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download web page (.html)" secondary="Standalone page: text, map view, panel image" secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
        </MenuItem>
        <MenuItem dense onClick={() => run('pdf', async () => downloadDocPdf(getDoc(), { includeMap: true, accent }))}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download PDF" secondary="Text report with map view" secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            run('png', async () => {
              if (!cardRef.current) return;
              const ok = await downloadHudCardPng(cardRef.current, getDoc().title, solidBackground);
              if (!ok) flash('Image export failed');
            })
          }
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><PngIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download image (.png)" secondary="Full panel, not just what's visible" secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
        </MenuItem>
        <MenuItem
          dense
          onClick={() =>
            run('pptx', async () => {
              const doc = getDoc();
              const img = cardRef.current ? await hudCardToPngDataUrl(cardRef.current, solidBackground) : null;
              await exportHudDocToPowerPoint({
                doc,
                cardImageDataUrl: img,
                includeMap: true,
                colors: { bg: '14151B', text: 'E6E8EC', muted: 'AEB7C2', accent: accent.replace('#', '') },
              });
            })
          }
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><PptxIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Export PowerPoint (.pptx)" secondary="Title, map view, content, panel image" secondaryTypographyProps={{ sx: { color: '#aaa' } }} />
        </MenuItem>
      </Menu>
    </>
  );
};

/** Minimized representation of a HUD card. */
export const HudChip: React.FC<{
  id: HudPanelId;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  defaultSx: SxProps<Theme>;
  zIndex: number;
  onExpand: () => void;
}> = ({ id, icon, label, tooltip, defaultSx, zIndex, onExpand }) => {
  const hud = useHudStyle();
  const drag = useHudDrag(id, defaultSx);
  return (
    <Tooltip title={tooltip} placement="right">
      <Box
        data-hud-card=""
        role="button"
        {...drag.handleProps}
        onClick={() => {
          if (!drag.dragging) onExpand();
        }}
        sx={{
          ...(drag.positionSx as object),
          ...(hud.surfaceSx as object),
          zIndex,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          height: 34,
          cursor: 'pointer',
          '&:hover': { borderColor: hud.accent },
        }}
      >
        <Box sx={{ display: 'flex', color: hud.accent }}>{icon}</Box>
        <Typography sx={{ fontSize: hud.fs(0.75), fontWeight: 700, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};
